import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Modal, Select, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { AlertTriangle, Download, FileInput, Lock, Search, Unlock } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { branchById, normText, positionById, toDay } from './hrLogic';
import {
  ASSIGN_STATE, PUNCH_SRC, WD, fmtMonth, isLocked, lockCheck, monthDays, monthOf, shiftLabel, sheetEmployees, stdDays, timesheetRow, type TsDay, type TsRow,
} from './tpLogic';
import { Badge, EmpCell, Notes, PageHeader, exportXlsx } from './hrUi';
import { BranchSelect, MonthNav, RequestModal, done, useTpPerms } from './tpUi';

const LEGEND: [string, string, string][] = [
  ['x', 'bg-emerald-50 text-emerald-700', 'Đủ công'], ['x', 'bg-amber-50 text-amber-700', 'Muộn / về sớm'], ['Q', 'bg-orange-100 text-orange-700', 'Quên chấm'],
  ['V', 'bg-rose-100 text-rose-700', 'Vắng không đơn'], ['P', 'bg-violet-50 text-violet-700', 'Phép năm / việc riêng (R)'], ['KL', 'bg-gray-100 text-gray-600', 'Không lương · Ô ốm · TS thai sản'],
  ['L', 'bg-rose-50 text-rose-700', 'Lễ (L+ có đi làm)'], ['·', '', 'Không có ca'],
];

/** Bảng công tháng tính từ lịch ca + giờ vào/ra + đơn từ; chốt công từng chi nhánh để tính lương */
export default function TimeSheet() {
  const s = useHr();
  const t = useTp();
  const { canTimeEdit, canApprove, me } = useTpPerms();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => {
    const n = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const today = toDay();
  const month = params.get('m') || monthOf(today);
  const branchId = params.get('branch') || '';
  const q = params.get('q') || '';
  const only = params.get('only') || '';
  const [day, setDay] = useState<{ row: TsRow; d: TsDay } | null>(null);
  const [explain, setExplain] = useState<{ empId: string; assignId: string; date: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const all = useMemo(() => sheetEmployees(s, month, branchId).map(e => timesheetRow(s, t, e, month)), [s, t, month, branchId]);
  const rows = all.filter(r => (!q || normText(`${r.e.name} ${r.e.code}`).includes(normText(q))) && (!only || r.issues.length > 0));
  const dates = monthDays(month);
  const branches = s.branches.filter(b => (!branchId || b.id === branchId) && sheetEmployees(s, month, b.id).length);
  const std = stdDays(t.time, month);

  const lockModal = (bid: string) => {
    const v = lockCheck(s, t, month, bid, today);
    Modal.confirm({
      title: `Chốt công ${fmtMonth(month)} · ${branchById(s, bid)?.name}`,
      width: 520,
      content: <div className="space-y-2 text-sm"><div>Sau khi chốt, lịch ca, giờ chấm và đơn từ của tháng bị khóa; bảng công dùng để tính lương.</div><Notes errors={v.errors} warnings={v.warnings} /></div>,
      okText: 'Chốt công', cancelText: 'Thôi', okButtonProps: { disabled: v.errors.length > 0 },
      onOk: () => { done(tpApi.lockMonth(month, bid, me), 'Đã chốt công'); },
    });
  };
  const unlock = (bid: string) => Modal.confirm({
    title: `Mở chốt công ${fmtMonth(month)} · ${branchById(s, bid)?.short}?`, content: 'Bảng lương nháp của tháng sẽ cần tính lại.', okText: 'Mở chốt', cancelText: 'Thôi',
    onOk: () => { done(tpApi.unlockMonth(month, bid, me), 'Đã mở chốt'); },
  });

  /** File chấm công: cột Mã NV (hoặc mã chấm công) + Thời gian, hoặc Ngày + Giờ */
  const onFile = (f: File) => {
    const rd = new FileReader();
    rd.onload = ev => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array', cellDates: true });
        const data = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[wb.SheetNames[0]], { defval: '' });
        const key = (o: Record<string, unknown>, ...names: string[]) => { const k = Object.keys(o).find(x => names.some(n => normText(x).includes(n))); return k ? o[k] : ''; };
        const out = data.map(o => {
          const code = String(key(o, 'ma nv', 'ma ns', 'ma cham cong', 'code', 'ma'));
          const at = key(o, 'thoi gian', 'time', 'datetime');
          const date = key(o, 'ngay', 'date');
          const hour = key(o, 'gio', 'hour');
          const v = at ? dayjs(at as string | Date) : dayjs(`${dayjs(date as string | Date).format('YYYY-MM-DD')}T${hour instanceof Date ? dayjs(hour).format('HH:mm') : String(hour).slice(0, 5)}`);
          return { code, at: v.isValid() ? v.valueOf() : NaN };
        }).filter(r => r.code && !Number.isNaN(r.at));
        if (!out.length) { message.error('Không đọc được dòng nào — cần cột "Mã NV" và "Thời gian" (hoặc "Ngày" + "Giờ")'); return; }
        const r = tpApi.importPunches(out, 'import', me);
        done(r, `Đã ghép ${r.id} / ${out.length} lượt chấm vào ca`);
      } catch { message.error('File không đúng định dạng Excel / CSV'); }
    };
    rd.readAsArrayBuffer(f);
  };

  const doExport = () => exportXlsx(`bang_cong_${month}`, `Công ${fmtMonth(month)}`, rows.map(r => ({
    'Mã NS': r.e.code, 'Họ tên': r.e.name, 'Chi nhánh': branchById(s, r.e.branchId)?.short || '', 'Vị trí': positionById(s, r.e.positionId)?.name || '',
    ...Object.fromEntries(r.days.map(d => [dayjs(d.date).format('DD'), d.code])),
    'Công': r.work, 'Phép có lương': r.paidLeave, 'Lễ': r.holiday, 'Không lương': r.unpaid, 'Ốm': r.sick, 'Thai sản': r.maternity,
    'Vắng': r.absent, 'Quên chấm': r.forgot, 'Muộn (lần)': r.lateCount, 'Muộn+sớm (phút)': r.lateMin + r.earlyMin, 'Làm thêm (giờ)': r.ot.total,
    'Ngày hưởng lương': r.paidDays, 'Công chuẩn': r.std,
  })));

  const columns: ColumnsType<TsRow> = [
    { title: 'Nhân sự', key: 'e', fixed: 'left', width: 220, render: (_, r) => <EmpCell s={s} e={r.e} /> },
    ...dates.map(d => ({
      title: <div className={`text-center leading-tight ${t.time.weeklyOff.includes(dayjs(d).day()) ? 'text-rose-500' : ''}`}><div>{dayjs(d).format('DD')}</div><div className="text-[10px] font-normal">{WD[dayjs(d).day()]}</div></div>,
      key: d, width: 38, align: 'center' as const,
      onCell: () => ({ className: '!p-0' }),
      render: (_: unknown, r: TsRow) => {
        const x = r.days.find(y => y.date === d)!;
        return (
          <Tooltip title={x.title ? <span className="whitespace-pre-line">{x.title}</span> : undefined}>
            <button type="button" onClick={() => x.evals.length && setDay({ row: r, d: x })}
              className={`w-full h-9 text-[11px] font-semibold ${x.tone} ${x.evals.length ? 'cursor-pointer hover:ring-1 hover:ring-[#3e91ab]' : 'cursor-default'}`}>{x.code}</button>
          </Tooltip>
        );
      },
    })),
    { title: 'Công', key: 'w', width: 58, align: 'right', render: (_, r) => <b>{r.work}</b> },
    { title: 'Phép/Lễ', key: 'pl', width: 64, align: 'right', render: (_, r) => <span className="text-xs">{r.paidLeave}/{r.holiday}</span> },
    { title: 'Muộn', key: 'late', width: 62, align: 'right', render: (_, r) => (r.lateCount ? <Tooltip title={`${r.lateMin} phút muộn, ${r.earlyMin} phút về sớm`}><span className="text-amber-600 text-xs">{r.lateCount} lần</span></Tooltip> : <span className="text-gray-300">—</span>) },
    { title: 'Làm thêm', key: 'ot', width: 70, align: 'right', render: (_, r) => (r.ot.total ? <span className="text-xs">{r.ot.total}h</span> : <span className="text-gray-300">—</span>) },
    {
      title: 'Hưởng lương', key: 'paid', width: 96, align: 'right', fixed: 'right',
      render: (_, r) => (
        <div className="flex items-center justify-end gap-1">
          {r.issues.length > 0 && <Tooltip title={<div>{r.issues.map(i => <div key={i}>• {i}</div>)}</div>}><AlertTriangle size={13} className="text-amber-500" /></Tooltip>}
          <b>{r.paidDays}</b><span className="text-xs text-gray-400">/{r.std}</span>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Bảng chấm công" desc="Công từng ngày tính từ ca đã xếp, giờ vào/ra và đơn từ — chốt công từng chi nhánh trước khi tính lương"
          extra={<>
            {canTimeEdit && <Button icon={<FileInput size={14} />} onClick={() => fileRef.current?.click()}>Nhập file máy chấm công</Button>}
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
            <Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>
          </>} />

        <div className="flex flex-wrap items-center gap-2">
          <MonthNav month={month} onChange={m => go({ m: m === monthOf(today) ? null : m })} />
          <BranchSelect value={branchId} onChange={v => go({ branch: v || null })} />
          <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã" className="!w-[180px]" value={q} onChange={e => go({ q: e.target.value || null })} />
          <Select className="w-[190px]" value={only} onChange={v => go({ only: v || null })} options={[{ value: '', label: 'Tất cả nhân sự' }, { value: 'issue', label: 'Cần xử lý (quên chấm, vắng…)' }]} />
          <span className="text-xs text-gray-500">Công chuẩn {fmtMonth(month)}: <b>{std}</b> ngày</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {branches.map(b => {
            const lock = t.locks.find(l => l.month === month && l.branchId === b.id);
            return (
              <div key={b.id} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${lock ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800' : 'border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-800'}`}>
                <span className="font-medium">{b.short}</span>
                {lock ? <span className="text-xs text-emerald-700">Đã chốt {dayjs(lock.at).format('DD/MM')} · {lock.by}</span> : <span className="text-xs text-gray-500">Chưa chốt</span>}
                {canApprove && (lock
                  ? <Button size="small" type="text" icon={<Unlock size={12} />} onClick={() => unlock(b.id)}>Mở</Button>
                  : <Button size="small" icon={<Lock size={12} />} onClick={() => lockModal(b.id)}>Chốt công</Button>)}
              </div>
            );
          })}
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="small" rowKey={r => r.e.id} dataSource={rows} columns={columns} bordered scroll={{ x: 220 + dates.length * 38 + 350 }}
            pagination={{ pageSize: 30, showSizeChanger: false, showTotal: n => `${n} nhân sự` }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có nhân sự" /> }} />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
          {LEGEND.map(([c, tone, label]) => <span key={label} className="flex items-center gap-1"><span className={`inline-flex w-6 h-5 items-center justify-center rounded text-[10px] font-semibold border border-gray-100 ${tone}`}>{c}</span>{label}</span>)}
        </div>
      </div>

      {day && (
        <Modal open title={`${day.row.e.name} · ${WD[dayjs(day.d.date).day()]} ${dayjs(day.d.date).format('DD/MM/YYYY')}`} footer={null} onCancel={() => setDay(null)}>
          <div className="space-y-2 text-sm">
            {day.d.evals.map(x => (
              <div key={x.a.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-1">
                <div className="flex items-center gap-2"><b>{x.sh.name}</b><span className="text-xs text-gray-400">{shiftLabel(x.sh)} · {branchById(s, x.a.branchId)?.short}</span><Badge className={`${ASSIGN_STATE[x.state].badge} ml-auto`}>{ASSIGN_STATE[x.state].label}</Badge></div>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  Vào {x.a.inAt ? dayjs(x.a.inAt).format('HH:mm') : '—'} · Ra {x.a.outAt ? dayjs(x.a.outAt).format('HH:mm') : '—'}
                  {x.lateMin ? ` · muộn ${x.lateMin} phút` : ''}{x.earlyMin ? ` · về sớm ${x.earlyMin} phút` : ''}{x.work ? ` · ${x.work} công` : ''}
                  {x.a.src ? <span className="text-gray-400"> · {PUNCH_SRC[x.a.src]}</span> : null}
                </div>
                {x.a.note && <div className="text-xs text-gray-400">{x.a.note}</div>}
                {['forgot_in', 'forgot_out', 'absent', 'late', 'early', 'late_early'].includes(x.state) && !isLocked(t, month, day.row.e.branchId) && (
                  <Button size="small" onClick={() => { setExplain({ empId: day.row.e.id, assignId: x.a.id, date: x.a.date }); setDay(null); }}>Tạo đơn giải trình</Button>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
      {explain && <RequestModal init={{ kind: 'explain', empId: explain.empId, assignId: explain.assignId, date: explain.date }} onClose={() => setExplain(null)} />}
    </Layout>
  );
}

